import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../utils/firebase';

export default function PatientLogin() {
  const [isRegister, setIsRegister] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const savePatientToFirestore = async (user, name) => {
    const ref = doc(db, 'patients', user.uid);
    const existing = await getDoc(ref);
    if (!existing.exists()) {
      await setDoc(ref, {
        uid: user.uid,
        name: name || user.displayName || '',
        email: user.email,
        phone: '',
        patientId: `PAT_${user.uid.slice(0, 8)}_${Date.now()}`,
        createdAt: new Date().toISOString()
      });
    }
    const data = (await getDoc(ref)).data();
    localStorage.setItem('currentPatient', JSON.stringify(data));
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isRegister) {
        const result = await createUserWithEmailAndPassword(auth, form.email, form.password);
        await updateProfile(result.user, { displayName: form.name });
        await savePatientToFirestore(result.user, form.name);
      } else {
        const result = await signInWithEmailAndPassword(auth, form.email, form.password);
        await savePatientToFirestore(result.user, '');
      }
      navigate('/patient-portal');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') setError('Email already registered! Please login.');
      else if (err.code === 'auth/wrong-password') setError('Wrong password!');
      else if (err.code === 'auth/user-not-found') setError('Account not found! Please register.');
      else if (err.code === 'auth/weak-password') setError('Password must be at least 6 characters.');
      else setError('Something went wrong. Try again.');
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await savePatientToFirestore(result.user, result.user.displayName);
      navigate('/patient-portal');
    } catch (err) {
      setError('Google sign-in failed. Try again.');
    }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%', padding: '0.9rem 1.2rem',
    border: '1.5px solid rgba(122,158,126,0.3)',
    borderRadius: '12px', fontFamily: 'Jost, sans-serif',
    fontSize: '0.95rem', background: 'white',
    color: '#2c2c2c', outline: 'none', boxSizing: 'border-box'
  };

  const labelStyle = {
    fontSize: '0.82rem', color: '#4a7a50',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    fontWeight: 500, display: 'block', marginBottom: '0.4rem'
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #e8f0e8, #faf7f0)',
      fontFamily: 'Jost, sans-serif', padding: '2rem'
    }}>
      <div style={{
        background: 'white', borderRadius: '24px',
        padding: '2.5rem', width: '100%', maxWidth: '440px',
        boxShadow: '0 30px 80px rgba(0,0,0,0.1)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🌿</div>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', color: '#4a7a50', fontWeight: 700 }}>
            Patient Portal
          </h1>
          <p style={{ color: '#888', fontSize: '0.88rem', marginTop: '0.4rem' }}>
            {isRegister ? 'Create your account' : 'Sign in to view your records'}
          </p>
        </div>

        {/* Google Button */}
        <button onClick={handleGoogle} disabled={loading} style={{
          width: '100%', padding: '0.9rem', border: '1.5px solid #e0e0e0',
          borderRadius: '12px', background: 'white', cursor: 'pointer',
          fontFamily: 'Jost', fontSize: '0.95rem', color: '#333',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '0.8rem', marginBottom: '1.5rem', transition: 'all 0.3s'
        }}
          onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
          onMouseLeave={e => e.currentTarget.style.background = 'white'}
        >
          <svg width="20" height="20" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ flex: 1, height: '1px', background: '#e0e0e0' }} />
          <span style={{ color: '#aaa', fontSize: '0.85rem' }}>or</span>
          <div style={{ flex: 1, height: '1px', background: '#e0e0e0' }} />
        </div>

        {/* Toggle */}
        <div style={{ display: 'flex', background: '#f5f5f5', borderRadius: '12px', padding: '4px', marginBottom: '1.5rem' }}>
          {['Login', 'Register'].map((tab, i) => (
            <button key={tab} onClick={() => { setIsRegister(i === 1); setError(''); }}
              style={{
                flex: 1, padding: '0.6rem', border: 'none', cursor: 'pointer',
                borderRadius: '10px', fontFamily: 'Jost', fontSize: '0.9rem',
                background: isRegister === (i === 1) ? '#4a7a50' : 'transparent',
                color: isRegister === (i === 1) ? 'white' : '#666',
                transition: 'all 0.3s'
              }}>{tab}</button>
          ))}
        </div>

        <form onSubmit={handleEmailAuth}>
          {isRegister && (
            <div style={{ marginBottom: '1.2rem' }}>
              <label style={labelStyle}>Full Name</label>
              <input name="name" type="text" value={form.name} onChange={handle}
                placeholder="Your full name" required={isRegister} style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#7a9e7e'}
                onBlur={e => e.target.style.borderColor = 'rgba(122,158,126,0.3)'} />
            </div>
          )}

          <div style={{ marginBottom: '1.2rem' }}>
            <label style={labelStyle}>Email Address</label>
            <input name="email" type="email" value={form.email} onChange={handle}
              placeholder="your@email.com" required style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#7a9e7e'}
              onBlur={e => e.target.style.borderColor = 'rgba(122,158,126,0.3)'} />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>Password</label>
            <input name="password" type="password" value={form.password} onChange={handle}
              placeholder="••••••••" required style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#7a9e7e'}
              onBlur={e => e.target.style.borderColor = 'rgba(122,158,126,0.3)'} />
            {isRegister && <p style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.4rem' }}>Minimum 6 characters</p>}
          </div>

          {error && (
            <div style={{ background: 'rgba(255,100,100,0.1)', color: '#c0392b', padding: '0.8rem', borderRadius: '10px', fontSize: '0.88rem', marginBottom: '1rem', textAlign: 'center' }}>
              ❌ {error}
            </div>
          )}

          <button type="submit" disabled={loading} style={{
            width: '100%', padding: '1rem', border: 'none',
            background: '#4a7a50', color: 'white', borderRadius: '12px',
            fontFamily: 'Jost', fontSize: '1rem', cursor: 'pointer', transition: 'all 0.3s'
          }}>
            {loading ? '⏳ Please wait...' : isRegister ? '🌿 Create Account' : '🌿 Sign In'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <a href="/" style={{ color: '#7a9e7e', fontSize: '0.85rem', textDecoration: 'none' }}>← Back to Home</a>
        </div>
      </div>
    </div>
  );
}
